import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { nodesAPI } from '../lib/api';
import { Header } from '../components/layout/Header';
import { RichTextEditor, RichTextViewer } from '../components/editor/RichTextEditor';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Save, Edit2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function NodeDetailPage() {
  const { nodeId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [node, setNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({
    title: '',
    content: '',
  });

  useEffect(() => {
    fetchNode();
  }, [nodeId]);

  const fetchNode = async () => {
    setLoading(true);
    try {
      const response = await nodesAPI.getOne(nodeId);
      setNode(response.data);
      setEditData({
        title: response.data.title,
        content: response.data.content || '',
      });
    } catch (error) {
      console.error('Error fetching node:', error);
      toast.error('Failed to load node');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await nodesAPI.update(nodeId, {
        title: editData.title,
        content: editData.content,
      });
      
      setNode(prev => ({
        ...prev,
        title: editData.title,
        content: editData.content,
      }));
      
      setEditing(false);
      toast.success('Node updated!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update node';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const canEdit = isAuthenticated && user?.user_id === node?.author_id;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!node) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-slate-700">Node not found</h2>
            <Button onClick={() => navigate('/')} className="mt-4">
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="node-detail-page">
        <button
          onClick={() => navigate(`/q/${node.question_id}`)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Mindmap
        </button>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          {editing ? (
            <div className="space-y-6">
              <div>
                <Label htmlFor="title" className="text-slate-700">Node Title</Label>
                <Input
                  id="title"
                  value={editData.title}
                  onChange={(e) => setEditData(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1"
                  data-testid="edit-title-input"
                />
              </div>
              
              <div>
                <Label className="text-slate-700 mb-2 block">Content (One-Pager)</Label>
                <RichTextEditor
                  content={editData.content}
                  onChange={(content) => setEditData(prev => ({ ...prev, content }))}
                  placeholder="Add detailed content here... You can use headings, lists, quotes, and more."
                />
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditing(false);
                    setEditData({
                      title: node.title,
                      content: node.content || '',
                    });
                  }}
                  className="flex items-center gap-2"
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-accent flex items-center gap-2"
                  data-testid="save-btn"
                >
                  {saving ? (
                    <div className="spinner w-4 h-4"></div>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">{node.title}</h1>
                  <p className="text-sm text-slate-500 mt-2">
                    Created by {node.author_name} • {node.vote_score} votes
                  </p>
                </div>
                
                {canEdit && (
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                    className="flex items-center gap-2"
                    data-testid="edit-btn"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit
                  </Button>
                )}
              </div>
              
              <div className="border-t border-slate-200 pt-6">
                {node.content ? (
                  <RichTextViewer content={node.content} />
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <p className="text-lg">No detailed content yet</p>
                    {canEdit && (
                      <Button
                        onClick={() => setEditing(true)}
                        className="mt-4 btn-accent"
                        data-testid="add-content-btn"
                      >
                        Add Content
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
